import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowUpRight, BriefcaseBusiness, Sparkles, Share2, FileText, ChevronDown, Settings as SettingsIcon, LogOut, LayoutDashboard } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
};

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="brutal-card p-5 sm:p-6 transition-colors">
    <Icon size={20} strokeWidth={1.5} className="text-zinc-900" />
    <h3 className="font-heading text-lg font-semibold mt-4">{title}</h3>
    <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* nav */}
      <header className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={28} />
            <span className="label-tiny ml-1 hidden sm:inline">JOB TRACKER</span>
          </div>
          {loading ? (
            <div className="flex items-center gap-2" data-testid="landing-nav-loading">
              <Link to="/about" className="label-tiny text-zinc-600 hover:text-zinc-900 px-2 hidden sm:inline" data-testid="nav-about-link-loading">About</Link>
              <div className="w-7 h-7 bg-zinc-100 animate-pulse" />
            </div>
          ) : user ? (
            <div className="flex items-center gap-2">
              <Link to="/about" className="label-tiny text-zinc-600 hover:text-zinc-900 px-2 hidden sm:inline" data-testid="nav-about-link-auth">About</Link>
              <DropdownMenu>
              <DropdownMenuTrigger data-testid="landing-user-menu-trigger" className="flex items-center gap-2 px-2 py-1 border border-transparent hover:border-zinc-200 transition-colors">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 border border-zinc-200" />
                ) : (
                  <div className="w-7 h-7 bg-zinc-200" />
                )}
                <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate">{user.name || "Account"}</span>
                <ChevronDown size={14} className="text-zinc-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56 border-zinc-200">
                <div className="px-3 py-2 text-xs text-zinc-500 truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="landing-menu-dashboard">
                  <Link to="/dashboard" className="cursor-pointer">
                    <LayoutDashboard size={14} className="mr-2" /> Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="landing-menu-settings">
                  <Link to="/settings" className="cursor-pointer">
                    <SettingsIcon size={14} className="mr-2" /> Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="landing-menu-logout" className="cursor-pointer">
                  <LogOut size={14} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/about" className="label-tiny text-zinc-600 hover:text-zinc-900 px-2 hidden sm:inline" data-testid="nav-about-link">About</Link>
              <Button
                data-testid="nav-signin-btn"
                onClick={login}
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-9 px-4 text-sm"
              >
                Sign in
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* hero */}
      <section className="relative">
        <div className="grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-28">
          <div className="max-w-3xl">
            <div className="label-tiny mb-6">v1 · built for engineers</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Track every application.<br/>
              <span className="text-zinc-400">Ship better resumes.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 mt-6 max-w-xl leading-relaxed">
              A no-nonsense pipeline for the job hunt. Save listings, follow apply links,
              tailor your resume with AI, and share leads with the people who get it.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-cta-btn"
                onClick={login}
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-12 px-6 text-sm font-semibold"
              >
                Continue with Google <ArrowUpRight size={16} className="ml-2" />
              </Button>
              <span className="label-tiny">Free · No card required</span>
            </div>
          </div>

          {/* stats strip */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 border border-zinc-200">
            {[
              ["05", "Pipeline stages"],
              ["AI", "Resume tailoring"],
              ["1-click", "Cover letters"],
              ["∞", "Shareable leads"],
            ].map(([n, l]) => (
              <div
                key={l}
                className="p-5 sm:p-6 border-zinc-200 border-r last:border-r-0 odd:border-b sm:odd:border-b-0"
                data-testid={`stat-strip-${l.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="font-mono-jb text-2xl sm:text-3xl tracking-tight">{n}</div>
                <div className="label-tiny mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">What you get</h2>
          <span className="label-tiny hidden sm:block">Core / 04</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={BriefcaseBusiness} title="Pipeline tracking" desc="Saved → Applied → Interviewing → Offer / Rejected. One tap to update." />
          <Feature icon={Sparkles} title="AI resume tailor" desc="Rewrites your resume to match each role using Claude. Facts stay intact." />
          <Feature icon={FileText} title="Cover letters" desc="Three-paragraph letters generated from your resume and the target role." />
          <Feature icon={Share2} title="Share leads" desc="Generate a public link to share a job lead with friends. Private until shared." />
        </div>
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <span className="label-tiny">© ReInVocation</span>
          <Link to="/about" className="label-tiny text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="footer-about-link">About</Link>
          <a
            href="https://ko-fi.com/suddengenesis"
            target="_blank" rel="noopener noreferrer"
            data-testid="footer-kofi-link"
            className="label-tiny text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            ☕ Help fund the site
          </a>
          <span className="label-tiny">v1.0 · 2026</span>
        </div>
      </footer>
    </div>
  );
}
